import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { Chart } from "react-google-charts";

// @mui material components
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import CircularProgress from "@mui/material/CircularProgress";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { useMaterialUIController } from "context";
import { ESCURO } from "assets/coresTema";

/**
 * Componente universal para Google Charts integrado ao Material Dashboard
 * Suporta modo escuro (Dark Mode), 3D nativo, gráficos de Pizza, Rosca, Barras, Colunas, Linhas, etc.
 */
function GoogleChart({
  chartType,
  data,
  chart,
  options,
  icon,
  title,
  description,
  height,
  width,
  chartWrapperRef,
  onReady,
}) {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  // Resolve tipo do gráfico Google Charts
  const resolvedChartType = useMemo(() => {
    switch (chartType) {
      case "pie":
      case "pie3d":
      case "doughnut":
      case "PieChart":
        return "PieChart";
      case "bar":
      case "bar3d":
      case "column":
      case "vertical":
      case "ColumnChart":
        return "ColumnChart";
      case "hbar":
      case "hbar3d":
      case "horizontal":
      case "BarChart":
        return "BarChart";
      case "line":
      case "LineChart":
        return "LineChart";
      case "area":
      case "AreaChart":
        return "AreaChart";
      case "steppedArea":
      case "SteppedAreaChart":
        return "SteppedAreaChart";
      case "combo":
      case "ComboChart":
        return "ComboChart";
      case "treemap":
      case "TreeMap":
        return "TreeMap";
      case "scatter":
      case "ScatterChart":
        return "ScatterChart";
      case "gauge":
      case "Gauge":
        return "Gauge";
      case "geo":
      case "GeoChart":
        return "GeoChart";
      default:
        return chartType || "ColumnChart";
    }
  }, [chartType]);

  // Conversão de dados se vier no formato { labels, datasets }
  const { formattedData, extractedPalette } = useMemo(() => {
    if (Array.isArray(data) && data.length > 0) {
      return { formattedData: data, extractedPalette: [] };
    }

    if (!chart || !chart.labels || chart.labels.length === 0) {
      return { formattedData: null, extractedPalette: [] };
    }

    const labels = chart.labels || [];
    const datasets = Array.isArray(chart.datasets)
      ? chart.datasets
      : chart.datasets
      ? [chart.datasets]
      : [];
    const firstDataset = datasets[0] || {};
    const dataArr = firstDataset.data || [];
    const rawBgColors =
      (Array.isArray(chart?.colors) && chart.colors.length > 0 ? chart.colors : null) ||
      (Array.isArray(chart?.palette) && chart.palette.length > 0 ? chart.palette : null) ||
      (Array.isArray(chart?.backgroundColors) && chart.backgroundColors.length > 0 ? chart.backgroundColors : null) ||
      (Array.isArray(firstDataset.backgroundColors) && firstDataset.backgroundColors.length > 0 ? firstDataset.backgroundColors : null) ||
      (Array.isArray(firstDataset.backgroundColor) && firstDataset.backgroundColor.length > 0 ? firstDataset.backgroundColor : null) ||
      (typeof firstDataset.backgroundColor === "string" ? [firstDataset.backgroundColor] : []) ||
      [];
    const bgColors = rawBgColors.filter((c) => typeof c === "string" && /^#[0-9A-Fa-f]{3,8}$/.test(c.trim()));

    const isPieOrDoughnut =
      chartType === "pie" ||
      chartType === "pie3d" ||
      chartType === "doughnut" ||
      chartType === "PieChart";
    const isBar =
      chartType === "bar" ||
      chartType === "bar3d" ||
      chartType === "column" ||
      chartType === "vertical" ||
      chartType === "ColumnChart" ||
      chartType === "hbar" ||
      chartType === "hbar3d" ||
      chartType === "horizontal" ||
      chartType === "BarChart";

    const isCurrency = chart.isCurrency || chart.aggregateType === "sum";
    const counts = chart.counts || [];

    const formatValue = (num) => {
      if (typeof num !== "number" || isNaN(num)) return String(num || "");
      if (isCurrency) {
        return num.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
      }
      return num.toLocaleString("pt-BR");
    };

    const getAnnotation = (num, idx) => {
      const formatted = formatValue(num);
      if (isCurrency && counts && counts[idx] != null) {
        const count = counts[idx];
        const countText = count === 1 ? "1 caso" : `${count.toLocaleString("pt-BR")} casos`;
        return `${formatted} (${countText})`;
      }
      return formatted;
    };

    if (isPieOrDoughnut) {
      const totalVal = dataArr.reduce((sum, n) => sum + (Number(n) || 0), 0);
      const gData = [["Categoria", firstDataset.label || "Valor"]];
      labels.forEach((label, idx) => {
        const val = Number(dataArr[idx]) || 0;
        const pct = (totalVal > 0 ? (val / totalVal) * 100 : 0).toLocaleString("pt-BR", { minimumFractionDigits: 1, maximumFractionDigits: 1 });
        const countSuffix = (isCurrency && counts && counts[idx] != null)
          ? ` • ${counts[idx] === 1 ? "1 caso" : `${counts[idx].toLocaleString("pt-BR")} casos`}`
          : "";
        const legendLabel = `${String(label)} (${pct}%${countSuffix})`;
        const formattedWithCount = (isCurrency && counts && counts[idx] != null)
          ? `${formatValue(val)} (${counts[idx] === 1 ? "1 caso" : `${counts[idx].toLocaleString("pt-BR")} casos`})`
          : formatValue(val);
        gData.push([legendLabel, { v: val, f: formattedWithCount }]);
      });
      return {
        formattedData: gData,
        extractedPalette: Array.isArray(bgColors) ? bgColors : [],
      };
    }

    if (chartType === "treemap" || chartType === "TreeMap") {
      const gData = [
        [
          { label: "Categoria", type: "string" },
          { label: "Parente", type: "string" },
          { label: "Valor", type: "number" },
          { label: "Cor", type: "number" },
        ],
        ["Total", null, 0, 0],
      ];
      labels.forEach((label, idx) => {
        const val = Number(dataArr[idx]) || 0;
        const annotation = getAnnotation(val, idx);
        gData.push([`${String(label)}: ${annotation}`, "Total", val, idx + 1]);
      });
      return {
        formattedData: gData,
        extractedPalette: bgColors,
      };
    }

    if (chartType === "combo" || chartType === "ComboChart") {
      const gData = [
        [
          { label: "Categoria", type: "string" },
          { label: firstDataset.label || "Colunas", type: "number" },
          { role: "style", type: "string" },
          { role: "annotation", type: "string" },
          { label: "Tendência", type: "number" },
        ],
      ];
      labels.forEach((label, idx) => {
        const val = Number(dataArr[idx]) || 0;
        const color = bgColors[idx % bgColors.length] || "#1A73E8";
        const formatted = formatValue(val);
        const annotation = getAnnotation(val, idx);
        gData.push([String(label), { v: val, f: formatted }, color, annotation, { v: val, f: formatted }]);
      });
      return {
        formattedData: gData,
        extractedPalette: bgColors,
      };
    }

    const isLineOrArea =
      chartType === "line" ||
      chartType === "LineChart" ||
      chartType === "area" ||
      chartType === "AreaChart" ||
      chartType === "steppedArea" ||
      chartType === "SteppedAreaChart";

    if (isLineOrArea && datasets.length <= 1 && Array.isArray(bgColors) && bgColors.length > 0) {
      const gData = [
        [
          { label: "Categoria", type: "string" },
          { label: firstDataset.label || "Valor", type: "number" },
          { role: "style", type: "string" },
          { role: "annotation", type: "string" },
        ],
      ];
      labels.forEach((label, idx) => {
        const val = Number(dataArr[idx]) || 0;
        const color = bgColors[idx % bgColors.length] || "#1A73E8";
        const formatted = formatValue(val);
        const annotation = getAnnotation(val, idx);
        const pointStyle = `point { size: 7; fill-color: ${color}; stroke-color: #FFFFFF; stroke-width: 1.5; }`;
        gData.push([String(label), { v: val, f: formatted }, pointStyle, annotation]);
      });
      return {
        formattedData: gData,
        extractedPalette: bgColors,
      };
    }

    if (isBar && datasets.length <= 1 && Array.isArray(bgColors) && bgColors.length > 0) {
      const gData = [
        [
          { label: "Categoria", type: "string" },
          { label: firstDataset.label || "Valor", type: "number" },
          { role: "style", type: "string" },
          { role: "annotation", type: "string" },
        ],
      ];
      labels.forEach((label, idx) => {
        const val = Number(dataArr[idx]) || 0;
        const color = bgColors[idx % bgColors.length] || "#1A73E8";
        const formatted = formatValue(val);
        const annotation = getAnnotation(val, idx);
        gData.push([String(label), { v: val, f: formatted }, color, annotation]);
      });
      return {
        formattedData: gData,
        extractedPalette: bgColors,
      };
    }

    // Múltiplos datasets, Linha, Área, etc.
    const headers = [{ label: "Categoria", type: "string" }];
    datasets.forEach((d, i) => {
      headers.push({ label: d.label || `Série ${i + 1}`, type: "number" });
      headers.push({ role: "annotation", type: "string" });
    });
    const gData = [headers];
    labels.forEach((label, idx) => {
      const row = [String(label)];
      datasets.forEach((d) => {
        const val = Number(d.data?.[idx]) || 0;
        const formatted = formatValue(val);
        const annotation = getAnnotation(val, idx);
        row.push({ v: val, f: formatted });
        row.push(annotation);
      });
      gData.push(row);
    });

    const multiColors = datasets
      .flatMap((d) => {
        const bg = d.backgroundColor || d.backgroundColors;
        if (Array.isArray(bg)) return bg;
        if (typeof bg === "string") return [bg];
        if (typeof d.borderColor === "string") return [d.borderColor];
        return [];
      })
      .filter((c) => typeof c === "string" && /^#[0-9A-Fa-f]{3,8}$/.test(c.trim()));

    const cleanBgColors = Array.isArray(bgColors) && bgColors.length > 0
      ? bgColors.filter((c) => typeof c === "string" && /^#[0-9A-Fa-f]{3,8}$/.test(c.trim()))
      : [];

    return {
      formattedData: gData,
      extractedPalette: cleanBgColors.length > 0 ? cleanBgColors : multiColors,
    };
  }, [data, chart, chartType]);

  // Opções combinadas com o tema Dark Mode
  const mergedOptions = useMemo(() => {
    const textColor = darkMode ? "#FFFFFF" : "#344767";
    const subTextColor = darkMode ? "#E0E3EB" : "#7b809a";
    const gridlineColor = darkMode ? "rgba(255, 255, 255, 0.12)" : "#f0f0f0";

    const defaultColors = [
      "#1A73E8",
      "#4CAF50",
      "#9C27B0",
      "#FF9800",
      "#F44336",
      "#00BCD4",
      "#E91E63",
      "#3F51B5",
      "#8BC34A",
      "#FFC107",
    ];

    const colorsToUse =
      extractedPalette.length > 0
        ? extractedPalette
        : options?.colors && Array.isArray(options.colors) && options.colors.length > 0
        ? options.colors.filter((c) => typeof c === "string" && /^#[0-9A-Fa-f]{3,8}$/.test(c.trim()))
        : defaultColors;

    const isPieOrDoughnutChart =
      chartType === "pie" ||
      chartType === "pie3d" ||
      chartType === "doughnut" ||
      chartType === "PieChart";

    const isHorizontalBar =
      chartType === "hbar" ||
      chartType === "hbar3d" ||
      chartType === "horizontal" ||
      chartType === "BarChart";

    const isColumnChart =
      chartType === "bar" ||
      chartType === "bar3d" ||
      chartType === "column" ||
      chartType === "vertical" ||
      chartType === "ColumnChart";

    const isLineChart =
      chartType === "line" ||
      chartType === "LineChart";

    const isAreaChart =
      chartType === "area" ||
      chartType === "AreaChart" ||
      chartType === "steppedArea" ||
      chartType === "SteppedAreaChart";

    const isComboChart =
      chartType === "combo" ||
      chartType === "ComboChart";

    const defaultChartArea = isPieOrDoughnutChart
      ? { left: "3%", top: "4%", width: "70%", height: "92%" }
      : isHorizontalBar
      ? { left: "24%", top: "5%", width: "54%", bottom: 45 } // sobra à direita para o rótulo da maior barra
      : isColumnChart || isLineChart || isAreaChart || isComboChart
      ? { left: "10%", top: "10%", width: "86%", bottom: 105 }
      : { left: "8%", top: "8%", width: "88%", bottom: 65 };

    const base = {
      // no escuro, a área do gráfico é mais funda que o bloco (ESCURO.grafico) para as cores se destacarem
      backgroundColor: darkMode ? ESCURO.grafico : "transparent",
      chartArea: options?.chartArea || defaultChartArea,
      colors: colorsToUse,
      pointSize: 7,
      pointsVisible: true,
      annotations: {
        alwaysOutside: true,
        highContrast: false,
        textStyle: {
          fontSize: 12,
          bold: true,
          color: darkMode ? "#FFFFFF" : "#2D3748",
          auraColor: darkMode ? ESCURO.painel : "#FAFAFA",
        },
        stem: {
          color: darkMode ? "rgba(255, 255, 255, 0.2)" : "#E0E0E0",
          length: 5,
        },
      },
      pieSliceTextStyle: {
        fontSize: 14,
        bold: true,
      },
      legend: {
        textStyle: { color: textColor, fontSize: 13 },
        position: isPieOrDoughnutChart ? "right" : "none",
        alignment: "center",
      },
      titleTextStyle: { color: textColor, fontSize: 16, bold: true },
      hAxis: {
        slantedText: isColumnChart || isLineChart || isAreaChart || isComboChart,
        slantedTextAngle: 45,
        showTextEvery: 1,
        maxAlternation: 1,
        maxTextLines: 2,
        minTextSpacing: 2,
        textStyle: { color: subTextColor, fontSize: 11, bold: false },
        titleTextStyle: { color: textColor, fontSize: 14, bold: true },
        gridlines: { color: gridlineColor },
        baselineColor: darkMode ? "rgba(255, 255, 255, 0.3)" : "#cccccc",
      },
      vAxis: {
        viewWindowMode: "pretty",
        textStyle: { color: subTextColor, fontSize: 12 },
        titleTextStyle: { color: textColor, fontSize: 14, bold: true },
        gridlines: { color: gridlineColor },
        baselineColor: darkMode ? "rgba(255, 255, 255, 0.3)" : "#cccccc",
      },
      tooltip: {
        textStyle: { fontSize: 14 },
        showColorCode: true,
      },
    };

    // Flags 3D (nativamente suportado pelo Google Charts para PieChart)
    base.is3D = chartType === "pie3d" || Boolean(options?.is3D);

    // Flag Rosca (Doughnut)
    if (chartType === "doughnut") {
      base.pieHole = 0.45;
    }

    // Configuração de ComboChart (Colunas + Linha de Tendência)
    if (isComboChart) {
      base.seriesType = "bars";
      base.series = {
        0: { type: "bars" },
        1: { type: "line", pointSize: 6, color: colorsToUse[1] || colorsToUse[0] || "#FF9800", curveType: "function" },
      };
    }

    // Configuração de Área
    if (isAreaChart) {
      base.areaOpacity = 0.35;
      base.pointSize = 6;
    }

    // Configuração de TreeMap
    if (chartType === "treemap" || chartType === "TreeMap") {
      const paletteToUse = colorsToUse.length >= 3 ? colorsToUse : defaultColors;
      base.minColor = paletteToUse[paletteToUse.length - 1] || (darkMode ? "#2D4B73" : "#C8E6C9");
      base.midColor = paletteToUse[Math.floor(paletteToUse.length / 2)] || (darkMode ? "#3F51B5" : "#4CAF50");
      base.maxColor = paletteToUse[0] || (darkMode ? "#1A73E8" : "#2E7D32");
      base.headerHeight = 0;
      base.showScale = true;
      base.textStyle = {
        color: "#FFFFFF",
        fontSize: 13,
        bold: true,
      };
    }

    // Barras e áreas medem tamanho: o eixo de valores começa no zero (sem isso, barras parecidas parecem muito diferentes)
    const valores = (Array.isArray(chart?.datasets) ? chart.datasets : [chart?.datasets]).flatMap((d) => d?.data || []);
    if (valores.every((v) => !(Number(v) < 0))) {
      if (isHorizontalBar) base.hAxis.viewWindow = { min: 0 };
      if (isColumnChart || isAreaChart || isComboChart) base.vAxis.viewWindow = { min: 0 };
    }
    // Contagem pequena (até 10): eixo só com inteiros, um a mais que o maior para o rótulo caber em cima da barra
    // (antes: 0,00 / 0,25 / 0,50...). A planilha usa a mesma escala (GraficoExcel)
    const contagem = chart?.aggregateType === "count";
    const maior = Math.max(0, ...valores.map((v) => Number(v) || 0));
    if (contagem && maior > 0 && maior <= 10 && !isPieOrDoughnutChart) {
      const topo = Math.ceil(maior) + 1;
      const eixo = { ticks: Array.from({ length: topo + 1 }, (_, i) => i), format: "#", viewWindow: { min: 0, max: topo } };
      if (isHorizontalBar) base.hAxis = { ...base.hAxis, ...eixo };
      else base.vAxis = { ...base.vAxis, ...eixo };
    }

    // Mesclar opções customizadas adicionais
    return {
      ...base,
      ...options,
      hAxis: { ...base.hAxis, ...options?.hAxis },
      vAxis: { ...base.vAxis, ...options?.vAxis },
      legend: { ...base.legend, ...options?.legend },
    };
  }, [darkMode, chartType, extractedPalette, options, chart]);

  // Eventos do Google Chart (captura de ref para exportação)
  const chartEvents = useMemo(
    () => [
      {
        eventName: "ready",
        callback: ({ chartWrapper, google }) => {
          if (chartWrapperRef) {
            if (typeof chartWrapperRef === "function") {
              chartWrapperRef(chartWrapper);
            } else if (typeof chartWrapperRef === "object") {
              chartWrapperRef.current = chartWrapper;
            }
          }
          if (onReady) {
            onReady({ chartWrapper, google });
          }
        },
      },
    ],
    [chartWrapperRef, onReady]
  );

  if (!formattedData || formattedData.length <= 1) {
    return (
      <MDBox
        display="flex"
        justifyContent="center"
        alignItems="center"
        height={height}
        width={width}
      >
        <MDTypography variant="button" color="text">
          Nenhum dado para exibir no gráfico.
        </MDTypography>
      </MDBox>
    );
  }

  const renderChart = (
    <MDBox
      py={title || description ? 2 : 0}
      px={title || description ? 2 : 0}
      width="100%"
      height="100%"
      minHeight={typeof height === "number" ? `${height}px` : (height && height !== "100%" ? height : "32rem")}
      display="flex"
      flexDirection="column"
    >
      {title || description ? (
        <MDBox display="flex" px={description ? 1 : 0} pt={description ? 1 : 0} mb={1}>
          {icon?.component && (
            <MDBox
              width="3.5rem"
              height="3.5rem"
              bgColor={icon.color || "info"}
              variant="gradient"
              coloredShadow={icon.color || "info"}
              borderRadius="xl"
              display="flex"
              justifyContent="center"
              alignItems="center"
              color="white"
              mt={-4}
              mr={2}
            >
              <Icon fontSize="medium">{icon.component}</Icon>
            </MDBox>
          )}
          <MDBox mt={icon?.component ? -1 : 0}>
            {title && (
              <MDTypography variant="h6" color={darkMode ? "white" : "dark"}>
                {title}
              </MDTypography>
            )}
            {description && (
              <MDBox mb={1}>
                <MDTypography component="div" variant="button" color={darkMode ? "secondary" : "text"}>
                  {description}
                </MDTypography>
              </MDBox>
            )}
          </MDBox>
        </MDBox>
      ) : null}

      <MDBox
        width="100%"
        height="100%"
        sx={{
          flex: 1,
          minHeight: typeof height === "number" ? `${height}px` : (height && height !== "100%" ? height : "30rem"),
          position: "relative",
          width: "100%",
        }}
      >
        <Chart
          key={`${resolvedChartType}-${chartType}-${Boolean(mergedOptions.is3D)}-${(formattedData || []).length}-${darkMode}-${(mergedOptions.colors || []).slice(0, 5).join("-")}`}
          chartType={resolvedChartType}
          width="100%"
          height="100%"
          data={formattedData}
          options={mergedOptions}
          chartEvents={chartEvents}
          loader={
            <MDBox display="flex" justifyContent="center" alignItems="center" height="100%" minHeight="20rem">
              <CircularProgress color="info" size={32} />
            </MDBox>
          }
        />
      </MDBox>
    </MDBox>
  );

  return title || description ? (
    <Card sx={{ backgroundColor: darkMode ? ESCURO.painel : "#ffffff" }}>
      {renderChart}
    </Card>
  ) : (
    renderChart
  );
}

GoogleChart.defaultProps = {
  chartType: "ColumnChart",
  data: null,
  chart: null,
  options: {},
  icon: null,
  title: "",
  description: "",
  height: "18rem",
  width: "100%",
  chartWrapperRef: null,
  onReady: null,
};

GoogleChart.propTypes = {
  chartType: PropTypes.string,
  data: PropTypes.arrayOf(PropTypes.array),
  chart: PropTypes.shape({
    labels: PropTypes.arrayOf(PropTypes.any),
    datasets: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.object), PropTypes.object]),
  }),
  options: PropTypes.objectOf(PropTypes.any),
  icon: PropTypes.shape({
    color: PropTypes.string,
    component: PropTypes.node,
  }),
  title: PropTypes.string,
  description: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  chartWrapperRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
  onReady: PropTypes.func,
};

export default GoogleChart;
